import request from 'tools/request'

// Interfaces for our data structures
export interface SafetyKPIs {
  totalEvents: number;
  eventsPer1kMiles: {
    fleetAverage: number;
    industryAverage: number;
  };
  mostCommonEvent: {
    eventType: string;
    percentage: number;
  };
  harshEventScore: {
    score: number;
    industryAverage: number;
  };
}

export interface EventTrendItem {
  date: string;
  totalEvents: number;
  eventsPer1kMiles: number;
}

export interface EventTrends {
  dailyTrends: EventTrendItem[];
}

export interface EventDistributionItem {
  eventType: string;
  totalCount: number;
  percentage: number;
}

export interface EventDistribution {
  totalEventCount: number;
  distributions: EventDistributionItem[];
}

export interface EventsByTimeOfDayItem {
  hour: string;
  count: number;
}

export interface EventsByTimeOfDay {
  eventsByHour: EventsByTimeOfDayItem[];
}

export interface EventsByLocation {
  totalEvents: number;
  safetyEvents: EventsByLocationItem[];
}

export interface EventsByLocationItem {
  id: number;
  type: string;
  time: string;
  speed: number;
  latitude: number;
  longitude: number;
  vin: string;
  driverName: string;
  driverFirstName: string;
  driverLastName: string;
  eldProvider: string;
}

export interface DriverStatItem {
  driverId: string;
  totalEvents: number;
  eventsPer1kMiles: number;
}

export interface DriverStats {
  driverStats: DriverStatItem[];
}

export interface DriverEvent {
  id: number;
  date: string;
  eventType: string;
  location: string;
  speed: string;
  vin: string;
  gForce: string;
  driverName: string;
  driverID: string;
  licenseNumber: string;
  phoneNumber: string;
}

export interface BreakdownEventDetail {
  eventId: number;
  eventType: string;
  eventCount: number;
  percentageOfTotal: number;
  countPer1k: number;
}

export interface BreakdownRow {
  id: number;
  rowId: string;
  rowName: string;
  miles: number;
  events: number;
  eventsDiff: number;
  eventsPer1k: number;
  eventsDetail: BreakdownEventDetail[];
}

export interface DriverVehicleBreakdown {
  breakdownByDriver: BreakdownRow[];
  breakdownByVehicle: BreakdownRow[];
}

export const getSafetyKPIs = async (carrierId: string, eventTypes: string[], startTime: string, endTime: string, useKilometers?: boolean): Promise<SafetyKPIs> => {
  const response = await request.get<SafetyKPIs>(
    `${process.env.REACT_APP_CARRIER_API_URL}carriers/${carrierId}/safety-events/kpis`,
    {
      params: {
        safetyEventType: eventTypes.join(','),
        startDateTime: startTime,
        endDateTime: endTime,
        ...(useKilometers !== undefined && { useKilometers }),
      }
    }
  );
  return response?.data;
};

export const getEventTrends = async (carrierId: string, eventTypes: string[], startTime: string, endTime: string, useKilometers?: boolean): Promise<EventTrends> => {
  const response = await request.get<EventTrends>(
    `${process.env.REACT_APP_CARRIER_API_URL}carriers/${carrierId}/safety-events/events-trends`,
    {
      params: {
        safetyEventType: eventTypes.join(','),
        startDateTime: startTime,
        endDateTime: endTime,
        ...(useKilometers !== undefined && { useKilometers }),
      }
    }
  );
  return response?.data;
};

export const getEventTypeDistribution = async (carrierId: string, eventTypes: string[], startTime: string, endTime: string): Promise<EventDistribution> => {
  const response = await request.get<EventDistribution>(
    `${process.env.REACT_APP_CARRIER_API_URL}carriers/${carrierId}/safety-events/events-distribution`,
    {
      params: {
        safetyEventType: eventTypes.join(','),
        startDateTime: startTime,
        endDateTime: endTime,
      }
    }
  );
  return response?.data;
};

export const getEventsByTimeOfDay = async (carrierId: string, eventTypes: string[], startTime: string, endTime: string): Promise<EventsByTimeOfDay> => {
  const response = await request.get<EventsByTimeOfDay>(
    `${process.env.REACT_APP_CARRIER_API_URL}carriers/${carrierId}/safety-events/events-by-time`,
    {
      params: {
        safetyEventType: eventTypes.join(','),
        startDateTime: startTime,
        endDateTime: endTime,
      }
    }
  );
  return response?.data;
};

export const getDriverVehicleBreakdown = async (
  carrierId: string,
  eventTypes: string[],
  startDateTime: string,
  endDateTime: string,
  useKilometers?: boolean
): Promise<DriverVehicleBreakdown> => {
  const response = await request.get<DriverVehicleBreakdown>(
    `${process.env.REACT_APP_CARRIER_API_URL}carriers/${carrierId}/safety-events/driver-vehicle-breakdown`,
    {
      params: {
        ...(eventTypes.length > 0 && { safetyEventType: eventTypes.join(',') }),
        startDateTime,
        endDateTime,
        ...(useKilometers !== undefined && { useKilometers }),
      }
    }
  );
  return response?.data;
};